/* eslint-disable camelcase */
import { ReactNode, useEffect, useState } from 'react';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowRightAltOutlined, PeopleAltOutlined } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { useFormContext } from 'react-hook-form';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import invoiceMissionService from 'src/shared/services/invoiceMissionService';
import { InvoiceMissionOptions } from 'src/shared/models/options/invoiceMissionOptions';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { InvoiceMissionTypeTextEnum } from 'src/shared/models/enums/invoiceMissionTypeText.enum';
import { getInvoiceTemplateById } from 'src/shared/services/invoiceService';
import { InvoiceTemplate, ProfileItemResponse } from 'src/modules/invoice/models/invoiceTemplate';
import { ProfileFilter, useSendingWizard } from 'src/modules/sending/contexts/sendingWizardContext';

// Tolkar aviseringens datumsträng (YYYYMMDD/YYYY-MM-DD) för läsbar etikett i väljaren.
const parseDate = (s?: string): Dayjs | null => {
  const t = (s ?? '').trim();
  if (/^\d{8}$/.test(t)) return dayjs(`${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)}`);
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return dayjs(t);
  return null;
};
const missionDate = (d?: string): string => {
  const p = parseDate(d);
  return p ? p.format('YYYY-MM-DD') : d ?? '';
};
const toFilter = (p: ProfileItemResponse): ProfileFilter => ({
  groupId: p.memberProfile_id,
  groupTitle: p.groupName,
  itemId: p.memberProfileItem_id,
  itemName: p.name,
});

// Chips-lista för en profil-uppsättning, eller tomtext (samma språk som formuläret) när tom.
const profileChips = (chips: { key: string; label: string }[], emptyText: string): ReactNode =>
  chips.length > 0 ? (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {chips.map((c) => (
        <Chip key={c.key} label={c.label} size='small' variant='outlined' />
      ))}
    </Box>
  ) : (
    <Typography variant='body2' color='text.secondary'>
      {emptyText}
    </Typography>
  );

interface SendingCopyFromMissionModalProps {
  open: boolean;
  onClose: () => void;
  // 'include' = "Ta bara med dem som även har profilerna" (kort 2), 'exclude' = "Undanta dem som har profilerna" (kort 3).
  variant: 'include' | 'exclude';
}

// Modal: jämför profilerna för DETTA steg ("Ta bara med dem som även har profilerna") mot en tidigare avisering.
// Vänster = den tidigare aviseringens profiler, höger = mitt nuvarande urval (endast utskrift, ingen redigering).
export const SendingCopyFromMissionModal = ({ open, onClose, variant }: SendingCopyFromMissionModalProps) => {
  const isMounted = useIsMounted();
  const { watch } = useFormContext();
  const ownerId = watch('owner_id') as number | undefined;
  const { profileFilters, setProfileFilters, excludeProfileFilters, setExcludeProfileFilters } = useSendingWizard();

  const isExclude = variant === 'exclude';
  const stepLabel = isExclude ? 'Undanta dem som har profilerna:' : 'Ta bara med dem som även har profilerna:';
  const emptyText = isExclude ? 'Inga undantag' : 'Inget profilvillkor';
  const currentItems = isExclude ? excludeProfileFilters : profileFilters;
  const setCurrent = isExclude ? setExcludeProfileFilters : setProfileFilters;

  const [missions, setMissions] = useState<InvoiceMissionResponse[]>([]);
  const [selected, setSelected] = useState<InvoiceMissionResponse | null>(null);
  const [template, setTemplate] = useState<InvoiceTemplate | null>(null);
  const [loading, setLoading] = useState(false);

  // Hämta Medlemsavisering-aviseringar för faktureringsgruppen när modalen öppnas (namnfilter klientsidan).
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const opts = new InvoiceMissionOptions();
        opts.pageIndex = 0;
        opts.pageSize = 200;
        if (ownerId) opts.ownerId = ownerId;
        const res = await invoiceMissionService.getAll(opts);
        // Filtrera på uppdragstyp "member" (medlemsaviseringar) – de kan vara olika namngivna.
        if (isMounted()) setMissions((res.data.results ?? []).filter((m) => m.type === InvoiceMissionTypeTextEnum.member));
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      }
    })();
  }, [open, ownerId, isMounted]);

  const handleClose = () => {
    setSelected(null);
    setTemplate(null);
    onClose();
  };

  const onSelect = async (m: InvoiceMissionResponse | null) => {
    setSelected(m);
    setTemplate(null);
    if (!m || !m.template_id) return;
    setLoading(true);
    try {
      const res = await getInvoiceTemplateById(m.template_id);
      if (isMounted()) setTemplate(res.data);
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const prevItems = isExclude ? template?.profilesExcludeItems ?? [] : template?.profilesIncludeItems ?? [];

  // Kopiera den tidigare aviseringens profiler till detta steg (endast profiler – rör inte medlemsår/andra steg).
  const apply = () => {
    setCurrent(prevItems.map(toFilter));
    handleClose();
  };

  const prevChips = prevItems.map((p) => ({ key: `prev-${p.memberProfileItem_id}`, label: `${p.groupName}: ${p.name}` }));
  const currentChips = currentItems.map((p) => ({ key: `cur-${p.itemId}`, label: `${p.groupTitle}: ${p.itemName}` }));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Jämför med tidigare aviseringar</DialogTitle>
      <DialogContent dividers>
        <Autocomplete
          size='small'
          options={missions}
          value={selected}
          onChange={(_e, v) => onSelect(v)}
          getOptionLabel={(m) => `${m.missionName} (${missionDate(m.date)})`}
          isOptionEqualToValue={(a, b) => a.invoiceMission_id === b.invoiceMission_id}
          renderInput={(params) => <TextField {...params} label='Välj tidigare avisering' />}
          fullWidth
          noOptionsText='Inga tidigare medlemsaviseringar'
        />

        {loading && (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {!template && !loading && (
          <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
            <PeopleAltOutlined sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
            <Typography variant='body2'>Välj en avisering ovan för att jämföra profilerna.</Typography>
          </Box>
        )}

        {template && !loading && (
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 44px 1fr' }, gap: 2, alignItems: 'stretch' }}>
              {/* Vänster: den tidigare aviseringens profiler */}
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography
                  variant='subtitle1'
                  sx={{ fontWeight: 700, color: 'text.secondary', pb: 1, mb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  Tidigare avisering
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 600, mb: 1 }}>
                  {stepLabel}
                </Typography>
                {profileChips(prevChips, emptyText)}
              </Box>

              {/* Pil (döljs på små skärmar där korten staplas) */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                <ArrowRightAltOutlined sx={{ fontSize: 28 }} />
              </Box>

              {/* Höger: mitt nuvarande urval (endast utskrift) */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'primary.light',
                  borderRadius: 1.5,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                }}
              >
                <Typography
                  variant='subtitle1'
                  sx={{ fontWeight: 700, color: 'primary.main', pb: 1, mb: 1.5, borderBottom: '1px solid', borderColor: 'primary.light' }}
                >
                  Nuvarande avisering
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 600, mb: 1 }}>
                  {stepLabel}
                </Typography>
                {profileChips(currentChips, emptyText)}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color='inherit' onClick={handleClose}>
          Stäng
        </Button>
        <Button variant='contained' disableElevation onClick={apply} disabled={!template}>
          Kopiera tidigare profilval
        </Button>
      </DialogActions>
    </Dialog>
  );
};
